"use client";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { handleApiErrorResponse } from "@/lib/apiErrorHandler";
import { PhoneValidator } from "@/lib/phoneValidator";

/**
 * Team Bible Reference - Section 10: Frontend Specification
 * Onboarding Step 4 patches PATCH /api/v1/user/profile with phone_number
 * Settings screen uses GET/PATCH /api/v1/user/profile
 */

export type UserProfile = {
  id?: string;
  email?: string;
  name?: string;
  phone_number?: string;
  phonenumber?: string;
  role?: string;
  referral_code?: string;
  referralCode?: string;
  organisationId?: string | number;
};

export type UserProfileResponse = {
  data?: UserProfile;
};

/**
 * Hook to get user profile
 */
export function useUserProfile() {
  const { status } = useSession();

  return useQuery({
    queryKey: ["userProfile"],
    queryFn: async () => {
      const response = await fetch("/api/v1/user/profile");
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = await response.json();
      return result.data ?? result;
    },
    enabled: status === "authenticated",
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to update user profile (phone number, name, etc.)
 * Used in Onboarding Step 4 and Settings
 */
export function useUpdateUserProfileMutation() {
  return useMutation({
    mutationFn: async (payload: Partial<UserProfile>) => {
      const response = await fetch("/api/v1/user/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = await response.json();
      return result.data ?? result;
    },
  });
}

/**
 * Hook to update phone number during onboarding
 * Calls PUT /api/v1/user/{userId} with phone_number
 * Backend transforms phone_number to phoneNumber and proxies to PUT /api/v1/internal/users/{userId}
 */
export function useUpdatePhoneNumberMutation() {
  return useMutation({
    mutationFn: async ({
      userId,
      phoneNumber,
    }: {
      userId: string;
      phoneNumber: string;
    }) => {
      const normalizedPhone = PhoneValidator.ensureCountryCode(phoneNumber);
      const response = await fetch(`/api/v1/user/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone_number: normalizedPhone }),
      });
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = await response.json();
      return result.data ?? result;
    },
  });
}

/**
 * Hook to complete user onboarding
 * Calls PUT /api/v1/user/{userId}/onboard
 */
export function useCompleteOnboardingMutation() {
  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(`/api/v1/user/${userId}/onboard`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ onboarded: true }),
      });
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = await response.json();
      return result.data ?? result;
    },
  });
}

/**
 * Hook to send OTP for phone verification
 * Calls POST /api/v1/onboarding/otp/send
 */
export function useSendOtpMutation() {
  return useMutation({
    mutationFn: async ({
      channel,
      email,
      phoneNumber,
    }: {
      channel: "EMAIL" | "SMS";
      email?: string;
      phoneNumber?: string;
    }) => {
      const normalizedPhone = phoneNumber
        ? PhoneValidator.ensureCountryCode(phoneNumber)
        : phoneNumber;
      const payload =
        channel === "EMAIL"
          ? { channel, email, phoneNumber: normalizedPhone }
          : { channel, phoneNumber: normalizedPhone };

      const response = await fetch("/api/v1/onboarding/otp/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = await response.json();
      return result.data ?? result;
    },
  });
}

/**
 * Hook to verify OTP
 * Calls POST /api/v1/onboarding/otp/verify
 */
export function useVerifyOtpMutation() {
  return useMutation({
    mutationFn: async ({
      channel,
      identifier,
      otp,
    }: {
      channel: "EMAIL" | "SMS";
      identifier: string;
      otp: string;
    }) => {
      const normalizedIdentifier =
        channel === "SMS"
          ? PhoneValidator.ensureCountryCode(identifier)
          : identifier;
      const response = await fetch("/api/v1/onboarding/otp/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channel, identifier: normalizedIdentifier, otp }),
      });
      if (!response.ok) throw await handleApiErrorResponse(response);
      const result = await response.json();
      return result.data ?? result;
    },
  });
}
